import BaseRepository from '@/api/BaseRepository';
import Project, { ProjectStage } from '@/models/Project';
import Customer from '@/models/Customer';

export type ProjectAttentionUrgentRow = {
    project: Partial<Project>;
    daysOverDue: number;
};

export type ProjectAttentionFollowUpRow = {
    project: Partial<Project>;
    daysOverValidity: number;
};

export type ProjectAttentionResponse = {
    urgentCount: number;
    followUpCount: number;
    urgentProjects: ProjectAttentionUrgentRow[];
    followUpProjects: ProjectAttentionFollowUpRow[];
    recentProjects: Partial<Project>[];
};

function serializeValveMaker(valveMaker?: Customer | string | { id?: string; name?: string } | null): { id?: string; name?: string } | null {
    if (!valveMaker) {
        return null;
    }
    if (typeof valveMaker === 'string') {
        const name = valveMaker.trim();
        return name ? { name } : null;
    }
    if (valveMaker.id) {
        return { id: valveMaker.id };
    }
    const name = valveMaker.name ? String(valveMaker.name).trim() : '';
    return name ? { name } : null;
}

function serializeCreatedAt(value?: string | number | null): number | undefined {
    if (value == null || value === '') {
        return undefined;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
    }
    const trimmed = String(value).trim();
    if (/^\d+$/.test(trimmed)) {
        const numeric = Number(trimmed);
        return Number.isFinite(numeric) ? numeric : undefined;
    }
    const timestamp = new Date(trimmed).getTime();
    return Number.isFinite(timestamp) ? timestamp : undefined;
}

function serializeCustomerId(customer?: Customer | string | { id?: string; name?: string } | null): string | null {
    if (!customer) {
        return null;
    }
    if (typeof customer === 'string') {
        const id = customer.trim();
        return id || null;
    }
    return customer.id ? String(customer.id) : null;
}

export default class ProjectRepository extends BaseRepository {

    private projectRoute = 'project';

    public async getAllProjects(customer: Customer): Promise<any> {
        return this.axiosClient.get(`${this.projectRoute}?customer=${customer.id}`);
    }

    public async getOverviewProjects(year?: number): Promise<{ year: number; years: number[]; projects: any[] }> {
        const query = year ? `?year=${year}` : '';
        return this.axiosClient.get(`${this.projectRoute}/overview${query}`);
    }

    /** Projekte des eingeloggten Users (assignee, sonst createdBy). */
    public async getAssignedProjects(): Promise<any> {
        return this.axiosClient.get(`${this.projectRoute}/assigned`);
    }

    public async getProjectAttention(recentLimit = 6): Promise<ProjectAttentionResponse> {
        return this.axiosClient.get(`${this.projectRoute}/attention?recentLimit=${recentLimit}`);
    }

    /*
    * public async getAllProjects(customer: Customer, page: number = 1, limit: number = 10): Promise<any> {
  return this.axiosClient.get(`${this.projectRoute}?customer=${customer.id}&page=${page}&limit=${limit}`);
}
* */

    public async getProject(projectId: string): Promise<any> {
        return this.axiosClient.get(`${this.projectRoute}/${projectId}`);
    }

    public async createProject(project: Project, customer: Customer): Promise<any> {
        try {
            const requestNumber = String(project.customerRequestNumber || project.anfrageNr || '').trim();
            const payload = {
                name: project.name,
                customer: customer.id,
                anfrageNr: requestNumber,
                customerRequestNumber: requestNumber,
                quotationCountry: project.quotationCountry,
                finalDestination: String(project.finalDestination || '').trim().toUpperCase(),
                quotationType: project.quotationType,
                valveMaker: serializeValveMaker(project.valveMaker),
                endUser: serializeCustomerId(project.endUser),
                epc: serializeCustomerId(project.epc),
                dueDate: project.dueDate ?? null,
                createdAt: serializeCreatedAt(project.createdAt),
                linkedProjectId: project.linkedProjectId ?? null
            };
            return await this.axiosClient.post(`${this.projectRoute}`, payload);
        } catch (error) {
            console.error('Axios error in createProject:', error);
            // throw new Error(error);
            throw error;
        }
    }

    public async updateProject(project: Project): Promise<any> {
        const requestNumber = String(project.customerRequestNumber || project.anfrageNr || '').trim();
        const payload = {
            name: project.name,
            anfrageNr: requestNumber,
            customerRequestNumber: requestNumber,
            quotationCountry: project.quotationCountry,
            finalDestination: String(project.finalDestination || '').trim().toUpperCase(),
            quotationType: project.quotationType,
            valveMaker: serializeValveMaker(project.valveMaker),
            endUser: serializeCustomerId(project.endUser),
            epc: serializeCustomerId(project.epc),
            dueDate: project.dueDate ?? null,
            createdAt: serializeCreatedAt(project.createdAt)
        };
        return this.axiosClient.patch(`${this.projectRoute}/${project.id}`, payload);
    }

    public setStage(projectId: string, stage: ProjectStage): Promise<any> {
        return this.axiosClient.put(`${this.projectRoute}/${projectId}/stage`, { stage });
    }

    public patchProjectMeta(
        projectId: string,
        meta: {
            dueDate?: number | null;
            assignee?: string | null;
            offerSentAt?: number | null;
            offerCreatedAt?: number | null;
            projectStatus?: string;
            lostReasonCode?: string | null;
            lostReasonCustom?: string | null;
            offerVolume?: number | null;
            offerPhase?: '' | 'ready' | 'created' | 'sent' | null;
            followUpOutcome?: '' | 'needs_time' | 'new_offer' | 'no_new_offer' | null;
            followUpNote?: string | null;
            followUpAt?: number | null;
            offerValidFrom?: number | null;
            offerRevisionId?: string | null;
        }
    ): Promise<any> {
        return this.axiosClient.patch(`${this.projectRoute}/${projectId}`, meta);
    }

    public async deleteProject(projectId: string): Promise<any> {
        return this.axiosClient.delete(`${this.projectRoute}/${projectId}`);
    }

    public getExcelPerformanceTable(projectId: string, revisionId: string): Promise<any> {
        return this.axiosClient.get(`${this.projectRoute}/${projectId}/performanceTable?revision=${revisionId}`, { responseType: 'arraybuffer' });
    }

    public getExcelPriceTable(projectId: string, revisionId: string): Promise<any> {
        return this.axiosClient.get(`${this.projectRoute}/${projectId}/priceTable?revision=${revisionId}`, { responseType: 'arraybuffer' });
    }

    public getModelDisplacement(projectId: string, revisionId: string): Promise<any> {
        return this.axiosClient.get(`${this.projectRoute}/${projectId}/model-displacement?revision=${revisionId}`);
    }

    public deleteAllActuators(projectId: string): Promise<any> {
        return this.axiosClient.delete(`${this.projectRoute}/deleteAllActuators/${projectId}`);
    }

    public getTotalProjectsAmount(): Promise<number> {
        return this.axiosClient.get(`${this.projectRoute}/total-projects-amount`);
    }

    public getNextOfferNumber(): Promise<{ angebotsNr: string }> {
        return this.axiosClient.get(`${this.projectRoute}/next-offer-number`);
    }

    public getProjectNames(): Promise<Array<{
        name: string;
        linkProjectId: string;
        customers: Array<{ id: string; name: string; projectId: string }>;
    }>> {
        return this.axiosClient.get(`${this.projectRoute}/names`);
    }

    /** Returns actuator label counts per year: { '2023': [...], '2024': [...], '2025': [...] } */
    public getActuatorLabelCounts(): Promise<Record<string, Array<{ label: string; count: number }>>> {
        return this.axiosClient.get(`${this.projectRoute}/actuator-label-counts`);
    }
}
